import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Siren, Phone } from './common/Icon';
import { telHref, dial } from '../utils/dialer';
import { isGuard } from '../utils/cachedUser';
import { trackEvent } from '../utils/clarity';
import ROUTER_CONSTANT from '../constants/RouterConstant';
import {
  getActiveSosAPI, acknowledgeSosAPI, resolveSosAPI, SosAlertDTO,
} from '../api/society/SosAPI';

import { Check } from 'lucide-react'
const POLL_MS = 15000;

const prefersReducedMotion = () =>
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function minutesAgo(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 'just now';
  const mins = Math.max(0, Math.floor((Date.now() - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins === 1) return '1 min ago';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return h === 1 ? '1 hour ago' : `${h} hours ago`;
}

const GlobalSOSAlertOverlay: React.FC = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState<SosAlertDTO[]>([]);
  const [busy, setBusy] = useState(false);
  const [, forceTick] = useState(0);
  const handledIdsRef = useRef<number[]>([]);

  const recordHandled = useCallback((id: number) => {
    if (!handledIdsRef.current.includes(id)) {
      handledIdsRef.current.push(id);
      if (handledIdsRef.current.length > 100) handledIdsRef.current.shift();
    }
  }, []);

  const markHandled = useCallback((id: number) => {
    recordHandled(id);
    setQueue(q => q.filter(a => a.id !== id));
  }, [recordHandled]);

  // Add-only: append genuinely new alerts (used by the single-event push path)
  const ingest = useCallback((alerts: SosAlertDTO[]) => {
    setQueue(prev => {
      const seen = new Set(prev.map(a => a.id));
      const additions = alerts.filter(a =>
        (a.status === 'ACTIVE' || a.status === 'ACKNOWLEDGED') &&
        !seen.has(a.id) &&
        !handledIdsRef.current.includes(a.id));
      if (additions.length === 0) return prev;
      return [...additions, ...prev].sort((a, b) => b.id - a.id);
    });
  }, []);

  // Full reconcile of the local queue against the server's active list (poll path):
  // drop alerts resolved/cancelled elsewhere, merge server status/category onto
  // survivors, and add genuinely new ones.
  const reconcile = useCallback((alerts: SosAlertDTO[]) => {
    const byId = new Map(alerts.map(a => [a.id, a]));
    setQueue(prev => {
      const survivors: SosAlertDTO[] = [];
      for (const a of prev) {
        const server = byId.get(a.id);
        if (!server) {
          // gone from the server list — resolved/cancelled elsewhere
          recordHandled(a.id);
          continue;
        }
        survivors.push({
          ...a,
          status: server.status,
          category: server.category,
          acknowledgedAt: server.acknowledgedAt,
          resolvedAt: server.resolvedAt,
        });
      }
      const seen = new Set(survivors.map(a => a.id));
      const additions = alerts.filter(a =>
        (a.status === 'ACTIVE' || a.status === 'ACKNOWLEDGED') &&
        !seen.has(a.id) &&
        !handledIdsRef.current.includes(a.id));
      return [...additions, ...survivors].sort((a, b) => b.id - a.id);
    });
  }, [recordHandled]);

  // Poll (guards only — checked per tick so first-login guards aren't missed)
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      if (!isGuard()) return;
      try {
        const alerts = await getActiveSosAPI(navigate);
        if (!stop) reconcile(alerts);
      } catch { /* transient — next tick retries */ }
    };
    tick();
    const h = window.setInterval(tick, POLL_MS);
    return () => { stop = true; window.clearInterval(h); };
  }, [navigate, reconcile]);

  // Foreground push
  useEffect(() => {
    const handler = (e: Event) => {
      if (!isGuard()) return;
      const extra = (e as CustomEvent).detail?.extra;
      const id = Number(extra?.sosId);
      if (!id || handledIdsRef.current.includes(id)) return;
      const status = (extra?.status as SosAlertDTO['status']) || 'ACTIVE';
      if (status === 'RESOLVED' || status === 'CANCELLED') {
        markHandled(id);
        return;
      }
      ingest([{
        id,
        societyId: 0,
        residentName: String(extra?.residentName || 'A resident'),
        homeLabel: extra?.homeLabel ? String(extra.homeLabel) : null,
        mobile: String(extra?.mobile || ''),
        category: (extra?.category || null) as SosAlertDTO['category'],
        status,
        raisedAt: String(extra?.raisedAt || new Date().toISOString()),
        acknowledgedAt: null,
        resolvedAt: null,
      }]);
    };
    window.addEventListener('sosAlertRaised', handler);
    return () => window.removeEventListener('sosAlertRaised', handler);
  }, [ingest, markHandled]);

  // Re-render every 30s so "x min ago" stays fresh while an alert is open
  useEffect(() => {
    if (queue.length === 0) return;
    const h = window.setInterval(() => forceTick(n => n + 1), 30000);
    return () => window.clearInterval(h);
  }, [queue.length]);

  const current = queue[0];

  const onAcknowledge = useCallback(async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      const ok = await acknowledgeSosAPI(navigate, current.id);
      if (ok) {
        trackEvent('sos_alert_acknowledged');
        setQueue(q => q.map(a => a.id === current.id ? { ...a, status: 'ACKNOWLEDGED' } : a));
      }
      else markHandled(current.id); // dead alert (e.g. cancelled → 422) — clear it
    } finally { setBusy(false); }
  }, [current, busy, navigate, markHandled]);

  const onResolve = useCallback(async () => {
    if (!current || busy) return;
    setBusy(true);
    try {
      const ok = await resolveSosAPI(navigate, current.id);
      if (ok) trackEvent('sos_alert_resolved');
      if (ok) markHandled(current.id);
      else markHandled(current.id); // dead alert (e.g. cancelled → 422) — clear it
    } finally { setBusy(false); }
  }, [current, busy, navigate, markHandled]);

  // One event per alert shown to the guard
  const currentId = current?.id;
  useEffect(() => {
    if (currentId) trackEvent('sos_alert_viewed');
  }, [currentId]);

  const tel = useMemo(() => telHref(current?.mobile), [current?.mobile]);

  if (!current) return null;

  const active = current.status === 'ACTIVE';
  const calm = prefersReducedMotion();

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(60,0,0,0.72)',
      display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10001,
    }}>
      <div style={{
        background: '#fff', borderRadius: 22, padding: 22, width: '90%', maxWidth: 380,
        border: `2px solid ${active ? '#dc2626' : '#047857'}`, boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
        position: 'relative', maxHeight: '85vh', overflowY: 'auto',
        transition: calm ? 'none' : 'border-color 0.5s ease',
        // Ring pulses only while nobody has acknowledged; decoration only, never blocks taps.
        animation: active && !calm ? 'ringPulseRed 1.6s infinite' : undefined,
      }}>
        {/* Dismiss — backstop so a guard is never trapped even if reconcile lags */}
        <button onClick={() => markHandled(current.id)} style={{
          position: 'absolute', top: '12px', right: '12px',
          background: 'rgba(255,255,255,0.95)', border: 'none',
          fontSize: '16px', cursor: 'pointer', width: '28px', height: '28px',
          borderRadius: '50%', display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: '#666', zIndex: 1,
          boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
        }}>×</button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{
            width: 42, height: 42, borderRadius: 12, background: active ? '#fee2e2' : '#d1fae5',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: calm ? 'none' : 'background 0.5s ease',
            transformOrigin: '50% 90%',
            animation: active && !calm ? 'wobble 0.9s infinite' : undefined,
          }}>
            <Siren size={24} strokeWidth={2} color={active ? '#dc2626' : '#047857'} />
          </span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.8, color: active ? '#dc2626' : '#047857', transition: calm ? 'none' : 'color 0.5s ease' }}>
              {active ? 'EMERGENCY SOS' : 'ACKNOWLEDGED'}{queue.length > 1 ? ` · +${queue.length - 1} more` : ''}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>raised {minutesAgo(current.raisedAt)}</div>
          </div>
        </div>

        <div style={{ fontSize: 20, fontWeight: 800, color: '#111827' }}>
          {current.homeLabel || 'Flat unknown'}
        </div>
        <div style={{ fontSize: 15, color: '#374151', marginTop: 2 }}>{current.residentName}</div>

        <div style={{
          display: 'inline-block', marginTop: 8, fontSize: 11, fontWeight: 700,
          color: current.category ? '#92400e' : '#6b7280',
          background: current.category ? '#fef3c7' : '#f3f4f6',
          padding: '2px 10px', borderRadius: 999,
        }}>
          {current.category ? current.category : 'Type not specified'}
        </div>

        {/* Call + primary action on one row */}
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          {current.mobile && (
            <a href={tel} onClick={(e) => dial(e, current.mobile)} style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '13px 10px', borderRadius: 12, textDecoration: 'none',
              background: '#ecfdf5', color: '#047857', fontWeight: 800, fontSize: 14,
            }}>
              <Phone size={15} strokeWidth={2} /> Call
            </a>
          )}
          {current.status === 'ACTIVE' ? (
            <button disabled={busy} onClick={onAcknowledge} style={{
              flex: 1, padding: 13, borderRadius: 12, border: 'none',
              background: '#dc2626', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
            }}>
              {busy ? 'Working…' : 'Acknowledge'}
            </button>
          ) : (
            <button disabled={busy} onClick={onResolve} className="anim-pop" style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: 13, borderRadius: 12, border: 'none',
              background: '#047857', color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
            }}>
              <Check size={15} strokeWidth={3} className="tick-draw" /> {busy ? 'Working…' : 'Resolve'}
            </button>
          )}
        </div>
        {current.status === 'ACKNOWLEDGED' && (
          <div style={{ textAlign: 'center', fontSize: 11, color: '#6b7280', marginTop: 8 }}>
            Acknowledged — resolve once the situation is handled.
          </div>
        )}
        <button
          onClick={() => { markHandled(current.id); navigate(ROUTER_CONSTANT.GUARD.SOS_HISTORY); }}
          style={{
            display: 'block', margin: '12px auto 0', border: 'none', background: 'none',
            color: '#6b7280', fontSize: 12, fontWeight: 700, cursor: 'pointer',
            textDecoration: 'underline',
          }}>
          View SOS log
        </button>
      </div>
    </div>
  );
};

export default GlobalSOSAlertOverlay;
